import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  makeStyles,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Chip,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import BackButton from '../../common/BackButton';
import DeleteConfirmDialog from '../common/DeleteConfirmDialog';
import { MoffTrainingRecord, TrainingMenu } from '../../../constants/Records';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3),
    paddingTop: theme.spacing(8),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  userInfo: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
  },
  filterContainer: {
    marginBottom: theme.spacing(3),
  },
  formControl: {
    minWidth: 150,
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  tableHead: {
    backgroundColor: theme.palette.grey[200],
  },
  actionCell: {
    width: 120,
  },
  emptyMessage: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(4),
  },
  chipAdl: {
    backgroundColor: '#e3f2fd',
    color: '#1565c0',
  },
  chipLocomo: {
    backgroundColor: '#fff3e0',
    color: '#e65100',
  },
}));

interface MoffTrainingRecordListProps {
  institutionId: string;
  institutionName: string;
  userId: string;
  userName: string;
  uniqueId: string;
  records: MoffTrainingRecord[];
  menus: TrainingMenu[];
  executing: boolean;
  loaded: boolean;
  deleted: boolean;
  onFetch: (userId: string, yearMonth: string) => void;
  onDelete: (userId: string, startedAt: string) => void;
  onResetFlags: () => void;
}

const MoffTrainingRecordList: React.FC<MoffTrainingRecordListProps> = ({
  institutionId,
  institutionName,
  userId,
  userName,
  uniqueId,
  records,
  menus,
  executing,
  loaded,
  deleted,
  onFetch,
  onDelete,
  onResetFlags,
}) => {
  const classes = useStyles();
  const history = useHistory();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<MoffTrainingRecord | null>(null);

  // フィルター状態（年月のみ）
  const currentDate = new Date();
  const [yearMonth, setYearMonth] = useState(
    `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`,
  );

  // 初回取得
  React.useEffect(() => {
    if (uniqueId) {
      onFetch(uniqueId, yearMonth);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uniqueId, yearMonth]);

  // 削除成功時の処理
  React.useEffect(() => {
    if (deleted) {
      onResetFlags();
      setDeleteDialogOpen(false);
      setSelectedRecord(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleted]);

  const getMenuName = (trainingId: number): string => {
    const menu = menus.find(m => m.id === trainingId);
    return menu?.name || `種目ID: ${trainingId}`;
  };

  const getMenuType = (trainingId: number): 'adl' | 'locomo' | null => {
    const menu = menus.find(m => m.id === trainingId);
    return menu?.type || null;
  };

  const formatDateTime = (dateTimeStr: string): string => {
    try {
      const date = new Date(dateTimeStr.replace(' ', 'T'));
      return date.toLocaleString('ja-JP', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateTimeStr;
    }
  };

  const handleCreate = () => {
    history.push(`/institutions/${institutionId}/users/${userId}/records/mofftraining/create`);
  };

  const handleEdit = (record: MoffTrainingRecord) => {
    const encodedStartedAt = encodeURIComponent(record.started_at);
    history.push(`/institutions/${institutionId}/users/${userId}/records/mofftraining/${encodedStartedAt}/edit`);
  };

  const handleDeleteClick = (record: MoffTrainingRecord) => {
    setSelectedRecord(record);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (selectedRecord) {
      onDelete(uniqueId, selectedRecord.started_at);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedRecord(null);
  };

  const handleYearMonthChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setYearMonth(event.target.value as string);
  };

  // 年月の選択肢を生成（過去12ヶ月）
  const getYearMonthOptions = () => {
    const options: string[] = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const ym = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      options.push(ym);
    }
    return options;
  };

  // 日付でソート（新しい順）
  const sortedRecords = [...records].sort((a, b) => {
    return new Date(b.started_at).getTime() - new Date(a.started_at).getTime();
  });

  return (
    <div className={classes.root}>
      <BackButton />

      <Box className={classes.userInfo}>
        <Typography variant="subtitle2" color="textSecondary">
          施設名: {institutionName}
        </Typography>
        <Typography variant="h6">利用者: {userName} 様</Typography>
      </Box>

      <Box className={classes.header}>
        <Typography variant="h5">モフトレ記録</Typography>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleCreate}>
          新規作成
        </Button>
      </Box>

      <Grid container spacing={2} className={classes.filterContainer}>
        <Grid item>
          <FormControl variant="outlined" className={classes.formControl}>
            <InputLabel>年月</InputLabel>
            <Select value={yearMonth} onChange={handleYearMonthChange} label="年月">
              {getYearMonthOptions().map(ym => (
                <MenuItem key={ym} value={ym}>
                  {ym}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {executing && !loaded ? (
        <Box className={classes.loadingContainer}>
          <CircularProgress />
        </Box>
      ) : sortedRecords.length === 0 ? (
        <Paper className={classes.emptyMessage}>
          <Typography>記録がありません</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table>
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell>日時</TableCell>
                <TableCell>種別</TableCell>
                <TableCell>種目</TableCell>
                <TableCell align="right">回数</TableCell>
                <TableCell align="right">平均角度</TableCell>
                <TableCell align="right">平均時間</TableCell>
                <TableCell className={classes.actionCell}>操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRecords.map(record => {
                const menuType = getMenuType(record.training_id);
                return (
                  <TableRow key={`${record.user_id}-${record.started_at}`} hover>
                    <TableCell>{formatDateTime(record.started_at)}</TableCell>
                    <TableCell>
                      {menuType === 'adl' && <Chip label="ADL" size="small" className={classes.chipAdl} />}
                      {menuType === 'locomo' && <Chip label="ロコモ" size="small" className={classes.chipLocomo} />}
                      {!menuType && '-'}
                    </TableCell>
                    <TableCell>{getMenuName(record.training_id)}</TableCell>
                    <TableCell align="right">{record.count ?? '-'}</TableCell>
                    <TableCell align="right">{record.average_angle?.toFixed(1) ?? '-'}</TableCell>
                    <TableCell align="right">{record.average_duration?.toFixed(1) ?? '-'}</TableCell>
                    <TableCell className={classes.actionCell}>
                      <IconButton size="small" onClick={() => handleEdit(record)} title="編集">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDeleteClick(record)} title="削除">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        title="レコードの削除"
        content={
          selectedRecord && (
            <>
              <Typography>このレコードを削除しますか？</Typography>
              <Box mt={2}>
                <Typography variant="body2">日時: {formatDateTime(selectedRecord.started_at)}</Typography>
                <Typography variant="body2">種目: {getMenuName(selectedRecord.training_id)}</Typography>
              </Box>
            </>
          )
        }
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        executing={executing}
      />
    </div>
  );
};

export default MoffTrainingRecordList;
